import { useQuery } from "@tanstack/react-query";
import { Building2, Contact, Handshake, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { scopeToWorkspace } from "../dashboard/useDashboardMetrics";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { NAV_GROUPS } from "../navigation/navConfig";

interface SearchResults {
  contacts: {
    id: number;
    first_name: string;
    last_name: string;
    company_name: string | null;
  }[];
  companies: { id: number; name: string }[];
  deals: { id: number; name: string; stage: string }[];
}

const EMPTY_RESULTS: SearchResults = { contacts: [], companies: [], deals: [] };

/** Escape PostgREST `or=`-syntax control characters from user input. */
const sanitize = (q: string) => q.replace(/[,()"\\]/g, " ").trim();

const searchWorkspace = async (query: string): Promise<SearchResults> => {
  const supabase = getSupabaseClient();
  const q = sanitize(query);
  if (q.length < 2) return EMPTY_RESULTS;

  // RLS enforces isolation; the active-workspace scope narrows presentation
  // for users belonging to several workspaces.
  const [contacts, companies, deals] = await Promise.all([
    scopeToWorkspace(
      supabase
        .from("contacts_summary")
        .select("id, first_name, last_name, company_name"),
    )
      .or(
        `first_name.ilike.%${q}%,last_name.ilike.%${q}%,email_fts.ilike.%${q}%`,
      )
      .limit(5),
    scopeToWorkspace(supabase.from("companies").select("id, name"))
      .ilike("name", `%${q}%`)
      .limit(5),
    scopeToWorkspace(supabase.from("deals").select("id, name, stage"))
      .ilike("name", `%${q}%`)
      .limit(5),
  ]);

  return {
    contacts: contacts.data ?? [],
    companies: companies.data ?? [],
    deals: deals.data ?? [],
  };
};

/**
 * Gravun global search (Cmd/Ctrl+K). Searches contacts, companies and deals
 * today; jobs, invoices and estimates join as those modules ship.
 */
export const GlobalSearch = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  const { data: results = EMPTY_RESULTS } = useQuery({
    queryKey: ["gravun", "global-search", query, getActiveWorkspaceId()],
    queryFn: () => searchWorkspace(query),
    enabled: open && query.trim().length >= 2,
    placeholderData: (previous) => previous,
  });

  const go = (to: string) => {
    onOpenChange(false);
    setQuery("");
    navigate(to);
  };

  const navItems = NAV_GROUPS.flatMap((group) => group.items).filter(
    (item) => item.implemented,
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <CommandInput
        placeholder="Search contacts, companies, deals..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {query.trim().length >= 2
            ? "No results found."
            : "Type to search your workspace."}
        </CommandEmpty>

        {results.contacts.length > 0 && (
          <CommandGroup heading="Contacts">
            {results.contacts.map((contact) => (
              <CommandItem
                key={`contact-${contact.id}`}
                value={`contact-${contact.id}`}
                onSelect={() => go(`/contacts/${contact.id}/show`)}
              >
                <Contact className="size-4 text-muted-foreground" />
                <span>
                  {contact.first_name} {contact.last_name}
                </span>
                {contact.company_name ? (
                  <span className="text-muted-foreground">
                    · {contact.company_name}
                  </span>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.companies.length > 0 && (
          <CommandGroup heading="Companies">
            {results.companies.map((company) => (
              <CommandItem
                key={`company-${company.id}`}
                value={`company-${company.id}`}
                onSelect={() => go(`/companies/${company.id}/show`)}
              >
                <Building2 className="size-4 text-muted-foreground" />
                <span>{company.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.deals.length > 0 && (
          <CommandGroup heading="Deals">
            {results.deals.map((deal) => (
              <CommandItem
                key={`deal-${deal.id}`}
                value={`deal-${deal.id}`}
                onSelect={() => go(`/deals/${deal.id}/show`)}
              >
                <Handshake className="size-4 text-muted-foreground" />
                <span>{deal.name}</span>
                <span className="text-muted-foreground">· {deal.stage}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {query.trim().length < 2 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Go to">
              {navItems.map((item) => (
                <CommandItem
                  key={item.key}
                  value={`nav-${item.key}`}
                  onSelect={() => go(item.to)}
                >
                  <item.icon className="size-4 text-muted-foreground" />
                  <span>{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

/** Topbar trigger styled as a quiet search field with the shortcut hint. */
export const GlobalSearchTrigger = ({ onOpen }: { onOpen: () => void }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex h-8 w-full max-w-72 items-center gap-2 rounded-md border border-border bg-card px-2.5 text-[13px] text-muted-foreground shadow-none transition-colors hover:bg-accent"
  >
    <Search className="size-3.5" />
    <span className="flex-1 text-left">Search workspace...</span>
    <kbd className="pointer-events-none flex h-5 items-center gap-0.5 rounded border border-border-subtle bg-surface-muted px-1.5 font-sans text-[10px] font-medium text-muted-foreground">
      ⌘K
    </kbd>
  </button>
);
